import { Button, Card } from 'components/atoms';
import { FormInput, LabeledStatus, ViewToggleButton } from 'components/molecules';
import ListWrapper from 'components/molecules/list-wrapper/ListWrapper';
import { QueryFilter, TopBar } from 'components/organisms';
import { TopbarPage } from 'components/templates';
import { useComponentVisible } from 'hooks';
import { useIsUserBanned } from 'hooks/useIsUserBanned';
import { useStoreState } from 'providers/StoreStateContext';
import { useTranslation } from 'react-i18next';
import { PiList, PiPlus, PiSquaresFour } from 'react-icons/pi';
import { useNavigate } from 'react-router-dom';
import { useGetMusicProductsDigitalList } from 'services/music-product-digital';
import { IMusicProductDigitalSearch } from 'services/music-product-digital/types/query';
import { STATUS } from 'services/types';
import { BUTTON_VARIANT } from 'types/components/button/button.interface';
import { booleanSelectOptions, useEnumToSelectOptions } from 'utils/components/inputs/select';

import ConditionalWarningPopupButton from 'components/molecules/buttons/conditional-warning-popup-button/ConditionalWarningPopupButton';
import QueryFilterLabels from 'components/organisms/filters/query-form/labels/QueryFilterLabels';
import { useFormSave } from 'hooks/useFormSave';
import { useListViewById } from 'hooks/useListViewById';

const musicProductDigitalSearchInitialValues: IMusicProductDigitalSearch = {
  ean: '',
  artistName: '',
  artistNickname: '',
  isrc: '',
  title: '',
  status: undefined,
};

const MusicProductsDigital = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { ref, isComponentVisible, handleComponentVisible } =
    useComponentVisible<HTMLDivElement>(false);

  const searchId = 'MusicProductsDigital2';
  const viewId = 'musicProductsDigital';

  const i18nextPrefix = 'music-product-digital.list';

  const { searchById, setSearchById } = useStoreState();
  const filters = searchById[searchId] || musicProductDigitalSearchInitialValues;

  const { view } = useListViewById(viewId, 'grid');

  const { data, isLoading, fetchNextPage, isFetchingNextPage } =
    useGetMusicProductsDigitalList(filters);

  const { getSelectOptions } = useEnumToSelectOptions();

  const { isBanned } = useIsUserBanned();

  const { isAnyFormSaved } = useFormSave();
  const { clearForms } = useStoreState();

  return (
    <TopbarPage
      topbarAbsolute
      topbar={
        <TopBar
          title={t(`${i18nextPrefix}.title`)}
          path=""
          customBackItem={<div />}
          topbarContent={
            <>
              <div className="flex">
                <ViewToggleButton id={viewId} />
                <QueryFilter.ButtonOpener
                  i18nextPrefix={i18nextPrefix}
                  onClick={handleComponentVisible}
                  variant={BUTTON_VARIANT.default}
                  isClear
                />
              </div>

              {!isBanned && (
                <ConditionalWarningPopupButton
                  title={t('popup.new-form-cancelation-title')}
                  message={t('popup.new-form-cancelation-description')}
                  confirmButtonVariant={BUTTON_VARIANT.background}
                  showWarning={isAnyFormSaved}
                  onConfirm={(a) => {
                    clearForms();
                    navigate('create/tracks', { replace: false });
                  }}
                  onlyIcon
                  variant={BUTTON_VARIANT.submit}
                >
                  <Button.Icon icon={<PiPlus />} />
                  <Button.Description description={t('button.add')} />
                </ConditionalWarningPopupButton>
              )}
            </>
          }
        >
          <QueryFilter<IMusicProductDigitalSearch>
            i18nextPrefix={i18nextPrefix}
            initialValues={filters}
          >
            <>
              <QueryFilter.Form<IMusicProductDigitalSearch>
                ref={ref}
                isOpen={isComponentVisible}
                onDebouncedFormChange={(values) => setSearchById(searchId, values)}
              >
                <QueryFilter.InputsGrid>
                  <FormInput.Select
                    appBackground
                    name="status"
                    label={t('input.label.status')}
                    options={getSelectOptions(STATUS, 'enum.STATUS')}
                  />
                  <FormInput.Text appBackground name="title" label={t('input.label.title')} />
                  <FormInput.Text
                    appBackground
                    name="artistNickname"
                    label={t('input.label.artistNickname')}
                  />
                  <FormInput.Text
                    appBackground
                    name="artistName"
                    label={t('input.label.artistName')}
                  />
                  <FormInput.Text
                    appBackground
                    name="ean"
                    label={t('input.label.ean')}
                    placeholder="0000000000"
                  />
                  <FormInput.Text
                    appBackground
                    name="isrc"
                    label="ISRC"
                    placeholder="USP031900001"
                  />
                  <FormInput.Select
                    appBackground
                    name="deliveryDiscrepancy"
                    label={t('input.label.deliveryDiscrepancy')}
                    options={booleanSelectOptions}
                  />
                  <FormInput.Select
                    appBackground
                    name="hasDeliveryToAccept"
                    label={t('input.label.hasDeliveryToAccept')}
                    options={booleanSelectOptions}
                  />
                  <FormInput.Select
                    appBackground
                    name="hasDuplicatedArtist"
                    label={t('input.label.hasDuplicatedArtist')}
                    options={booleanSelectOptions}
                  />
                  <FormInput.Select
                    appBackground
                    name="hasContracts"
                    label={t('input.label.hasContracts')}
                    options={booleanSelectOptions}
                  />
                </QueryFilter.InputsGrid>
              </QueryFilter.Form>
              <QueryFilterLabels scope="musicProductsDigital" isFormOpen={isComponentVisible} />
            </>
          </QueryFilter>
        </TopBar>
      }
    >
      <ListWrapper
        view={view}
        items={data?.pages.flat()}
        isLoading={isLoading}
        isFetchingNextPage={isFetchingNextPage}
        item={(data) => (
          <Card
            onClick={() => navigate(`/music-product-digital/${data.id}/tracks`)}
            view={view}
            image={data.productImagePreviewLink ?? ''}
            header={data.mainTitle?.title ?? ''}
            description={data.mainTitle?.version ?? ''}
            status={<LabeledStatus status={data.releaseStatus} noname />}
          />
        )}
      />
    </TopbarPage>
  );
};

export default MusicProductsDigital;
